import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './utils/api';
import Stars from './Stars';
import HorizontalScroll from './HorizontalScroll';
import Reviews from './Reviews';
import { FaArrowLeft } from 'react-icons/fa';
import { FaArrowRight } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import './styles/common.css';
import './styles/product.css';

export default function Product(props) {
    const [product, setProduct] = useState({});
    const [selectedColorCode, setSelectedColorCode] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [searchParams] = useSearchParams();
    const [isActive, setActive] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [endNumber, setEndNumber] = useState(3);
    const [recommendations, setRecommendations] = useState([]);
    const [stars, setStars] = useState([]);
    const containerRef = useRef();
    const id = searchParams.get('id');
    const endpoint = 'shopxyz.online';
    const apiVersion = '1.0';
    const jwtToken = window.localStorage.getItem('aws_JWT');

    useEffect(() => {
        api.getProduct(id).then((json) => {
            setSelectedColorCode(json.data.colors[0].code);
            setProduct(json.data);
        });
    }, [searchParams]);

    useEffect(() => {
        fetchRecommendationHandler();
        fetchReviewHandler();
        checkInFavorite();
    }, []);

    const slideToLeft = () => {
        containerRef.current.scrollLeft -= 217;
    };

    const slideToRight = () => {
        containerRef.current.scrollLeft += 217;
    };

    if (Object.keys(product).length === 0) return null;

    const getStock = (colorCode, size) => {
        const target = product.variants.find((variant) => variant.color_code === colorCode && variant.size === size);
        if (target) return target.stock;
        return 0;
    };

    function renderProductColorSelector() {
        return (
            <div className="product__color-selector">
                {product.colors.slice(0, 4).map((color) => (
                    <div
                        key={color.code}
                        className={`product__color${color.code === selectedColorCode ? ' product__color--selected' : ''}`}
                        style={{ backgroundColor: `#${color.code}` }}
                        onClick={() => {
                            setSelectedColorCode(color.code);
                            setSelectedSize('');
                            setQuantity(1);
                        }}
                    />
                ))}
            </div>
        );
    }

    function renderProductSizeSelector() {
        return (
            <div className="product__size-selector">
                {product.sizes.slice(0, 4).map((size) => {
                    const stock = getStock(selectedColorCode, size);
                    return (
                        <div
                            key={size}
                            className={`product__size${size === selectedSize ? ' product__size--selected' : ''}${stock === 0 ? ' product__size--disabled' : ''}`}
                            onClick={() => {
                                if (stock === 0) return;
                                setSelectedSize(size);
                                if (stock < quantity) setQuantity(1);
                            }}
                        >
                            {size}
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderProductQuantitySelector() {
        return (
            <div className="product__quantity-selector">
                <div
                    className="product__quantity-minus"
                    onClick={() => {
                        if (!selectedSize) return;
                        if (quantity === 1) return;
                        setQuantity(quantity - 1);
                    }}
                />
                <div className="product__quantity-value">{quantity}</div>
                <div
                    className="product__quantity-add"
                    onClick={() => {
                        if (!selectedSize) return;
                        const stock = getStock(selectedColorCode, selectedSize);
                        if (quantity === stock) return;
                        setQuantity(quantity + 1);
                    }}
                />
            </div>
        );
    }

    function addToCart() {
        if (!selectedSize) {
            window.alert('請選擇尺寸');
            return;
        }
        const newCartItems = [
            ...props.cartItems,
            {
                color: product.colors.find((color) => color.code === selectedColorCode),
                id: product.id,
                image: product.main_image,
                name: product.title,
                price: product.price,
                qty: quantity,
                on_sale: product.on_sale,
                size: selectedSize,
                stock: getStock(selectedColorCode, selectedSize),
            },
        ];
        window.localStorage.setItem('cart', JSON.stringify(newCartItems));
        props.setCartItems(newCartItems);
        window.alert('已加入購物車');
    }

    function handleToggle() {
        setActive(!isActive);

        if (!jwtToken) {
            window.alert('請先登入');
            window.location.replace('./profile');
        } else {
            if (isActive === false) {
                addToFavorite();
            } else {
                removeFromFavorite();
            }
        }
    }

    async function checkInFavorite() {
        if (jwtToken) {
            const response = await fetch(`https://${endpoint}/api/${apiVersion}/user/favorite?id=${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            });
            const data = await response.json();
            let idArray = [];
            data.forEach((item) => {
                idArray.push('?id=' + item.id);
            });

            if (idArray.includes(window.location.search)) {
                setActive(true);
            } else {
                setActive(false);
            }
        }
    }

    async function addToFavorite() {
        if (jwtToken) {
            await fetch(`https://${endpoint}/api/${apiVersion}/user/favorite?id=${id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            });
        }
    }

    async function removeFromFavorite() {
        if (jwtToken) {
            await fetch(`https://${endpoint}/api/${apiVersion}/user/favorite?id=${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwtToken}`,
                },
            });
        }
    }

    function loadmoreComments() {
        setEndNumber((prev) => prev + 3);
    }

    async function fetchRecommendationHandler() {
        const response = await fetch(`https://${endpoint}/api/${apiVersion}/products/recommended?id=${id}`);
        const data = await response.json();
        const allRecommendations = data.data.map((recommendPhoto) => {
            return {
                title: recommendPhoto.title,
                image: recommendPhoto.main_image,
                id: recommendPhoto.id,
            };
        });
        setRecommendations(allRecommendations);
    }

    async function fetchReviewHandler() {
        const response = await fetch(`https://${endpoint}/api/${apiVersion}/order/review?id=${id}`);
        const data = await response.json();
        setStars(Math.round(Number(data.avg_rating)));
        const reviews = data.review;
        setReviews(reviews);
    }

    return (
        <div className="product">
            <img src={product.main_image} className="product__main-image" alt="product" />
            <div className={product.on_sale === 0 ? 'no__sale' : 'on__sale'}>SALE</div>
            <div className="product__detail">
                <div className="product__title">
                    {product.title} <div className={isActive ? 'save save__saved' : ' save save__empty'} onClick={handleToggle}></div>
                </div>
                <div className="product__id">{product.id}</div>
                <div className="product__price">TWD.{product.price}</div>
                <div className="star-section">
                    <Stars stars={stars} />
                </div>
                <div className="product__variant">
                    <div className="product__color-title">顏色｜</div>
                    {renderProductColorSelector()}
                </div>
                <div className="product__variant">
                    <div className="product__size-title">尺寸｜</div>
                    {renderProductSizeSelector()}
                </div>
                <div className="product__variant">
                    <div className="product__quantity-title">數量｜</div>
                    {renderProductQuantitySelector()}
                </div>
                <button className="product__add-to-cart-button" onClick={addToCart}>
                    {selectedSize ? '加入購物車' : '請選擇尺寸'}
                </button>
                <div className="product__note">{product.note}</div>
                <div className="product__texture">{product.texture}</div>
                <div className="product__description">{product.description}</div>
                <div className="product__place">素材產地 / {product.place}</div>
                <div className="product__place">加工產地 / {product.place}</div>
            </div>
            <div className="product__story">
                <div className="product__story-title">細部說明</div>
                <div className="product__story-content">{product.story}</div>
            </div>
            <div className="product__images">
                {product.images.map((image, index) => (
                    <img src={image} className="product__image" key={index} alt="product" />
                ))}
                <div className="product__story">
                    <div className="product__story-title previous__reviews">過往評價</div>
                </div>
                {reviews.slice(0, endNumber).map((review) => {
                    return <Reviews key={uuidv4()} username={review.username} time={review.time} rating={review.rating} comment={review.comment} />;
                })}
                {endNumber >= reviews.length ? null : (
                    <div className="more__reviews">
                        <button className="review__more__button" onClick={loadmoreComments}>
                            查看更多
                        </button>
                    </div>
                )}
                <div className="product__story">
                    <div className="product__story-title">猜你喜歡</div>
                </div>
            </div>
            <div className="scroll__container">
                <div className="scroll__area">
                    <div className="left__arrow">
                        <FaArrowLeft className="left__arrow__icon" onClick={slideToLeft} />
                    </div>
                    <div className="scroll__section" ref={containerRef}>
                        {recommendations.map((rec) => {
                            return <HorizontalScroll key={uuidv4()} image={rec.image} title={rec.title} id={rec.id} />;
                        })}
                    </div>
                    <div className="right__arrow">
                        <FaArrowRight className="right__arrow__icon" onClick={slideToRight} />
                    </div>
                </div>
            </div>
        </div>
    );
}
